// «Апрувы в Meta» в настройках (заявка владельца #4): по апруву лида с нашей рекламы платформа отправляет
// в пиксель событие Purchase с выплатой сети и адресом лендинга оффера — Meta учится на выкупах, а не на всех
// заявках. Сначала «Проверка» с кодом из Events Manager → «Тестовые события», потом «Включено»; офферы можно
// выключать по одному. Ниже — сколько апрувов за 7 дней ушло, доступ к пикселю и качество совпадения (EMQ).
import { Button, Card, Chip, Segmented, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';

type Mode = 'off' | 'test' | 'live';

interface PixelCheck {
  pixelId: string;
  access: boolean;
  name: string | null;
  error: string | null;
  quality: Array<{ event: string; score: number | null; keys: Array<{ key: string; pct: number }> }>;
  at: string;
}

export interface CapiView {
  connected: boolean;
  mode: Mode;
  event: string;
  testCode: string | null;
  offers: Array<{ id: number; slug: string; name: string; on: boolean }>;
  stats: {
    approved: number;
    sent: number;
    sentTest: number;
    error: number;
    skipped: number;
    reasons: Array<{ reason: string; n: number }>;
  };
  check: { at: string; pixels: PixelCheck[] } | null;
}

const MODES = [
  { value: 'off', label: 'Выключено' },
  { value: 'test', label: 'Проверка' },
  { value: 'live', label: 'Включено' },
] as const;

const KEY = ['capi'];
const share = (n: number, of: number) => (of > 0 ? `${((n / of) * 100).toFixed(1)}%` : '—');

export function CapiCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<CapiView>('/api/integrations/meta/capi') });
  const d = q.data;
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const testCode = code ?? d?.testCode ?? '';

  const save = async (
    body: { mode: Mode; testCode?: string | null; offersOff?: number[] },
    done?: string,
  ) => {
    try {
      const r = await api<CapiView>('/api/integrations/meta/capi-approve', body, 'PUT');
      qc.setQueryData(KEY, r);
      setCode(null);
      if (done) toast(done);
    } catch (e) {
      toast((e as Error).message);
    }
  };
  const setMode = (mode: Mode) => {
    if (mode === 'test' && !testCode.trim()) {
      toast('Вставьте код из Events Manager → «Тестовые события»');
      return;
    }
    void save(
      { mode, ...(mode === 'test' ? { testCode: testCode.trim() } : {}) },
      mode === 'off'
        ? 'Апрувы в Meta больше не отправляются'
        : mode === 'test'
          ? 'Апрувы уходят в Meta с кодом проверки — смотрите «Тестовые события»'
          : 'Апрувы уходят в Meta',
    );
  };
  const toggleOffer = (id: number, on: boolean) => {
    if (!d) return;
    const offersOff = d.offers.filter((o) => (o.id === id ? !on : !o.on)).map((o) => o.id);
    void save({ mode: d.mode, offersOff });
  };
  const check = async () => {
    setBusy(true);
    try {
      qc.setQueryData(KEY, await api<CapiView>('/api/integrations/meta/capi-check', {}));
    } catch (e) {
      toast((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (q.isError)
    return (
      <Card title="Апрувы в Meta">
        <p className="muted af-lead">Не удалось загрузить настройку: {(q.error as Error).message}</p>
      </Card>
    );

  return (
    <Card title="Апрувы в Meta">
      <p className="muted af-lead">
        Сейчас Meta учится на всех заявках, в том числе на мусоре. Когда сеть подтверждает заказ, платформа
        сообщает об этом в пиксель событием «{d?.event ?? 'Purchase'}» — с выплатой сети и адресом лендинга
        оффера, как в ссылке объявления. Номер покупателя уходит только в зашифрованном виде. Только лиды с
        вашей рекламы, не старше 7 дней; каждый апрув — один раз.
      </p>
      {d && !d.connected ? (
        <p className="muted af-note">Сначала подключите Meta в карточке «Сеть и реклама».</p>
      ) : (
        <>
          <div className="af-hold">
            <span>Отправлять апрувы</span>
            <Segmented
              label="Отправка апрувов в Meta"
              value={d?.mode ?? 'off'}
              onChange={setMode}
              options={MODES.map((m) => ({ ...m }))}
            />
          </div>
          <form
            className="capi-code"
            onSubmit={(e) => {
              e.preventDefault();
              setMode('test');
            }}
          >
            <label htmlFor="capi-test-code">Код проверки</label>
            <input
              id="capi-test-code"
              className="input"
              value={testCode}
              onChange={(e) => setCode(e.target.value)}
              placeholder="TEST12345"
              autoComplete="off"
              spellCheck={false}
            />
            <Button type="submit" disabled={!testCode.trim()}>
              {d?.mode === 'test' ? 'Сохранить код' : 'Начать проверку'}
            </Button>
          </form>
          <p className="muted af-note">
            {d?.mode === 'test'
              ? 'Проверка: события видны в Events Manager → пиксель → «Тестовые события». Meta засчитывает их как обычные, поэтому после «Включено» те же апрувы второй раз не уйдут.'
              : d?.mode === 'live'
                ? 'Включено: каждый новый апрув уходит в Meta в течение 5 минут.'
                : 'Код — в Events Manager → пиксель → «Тестовые события». С ним события видны там сразу.'}
          </p>

          {d && (
            <table className="af-tbl">
              <caption>Апрувы с вашей рекламы за 7 дней</caption>
              <tbody>
                <tr>
                  <td>Ушли в Meta</td>
                  <td className="num">
                    {d.stats.sent} из {d.stats.approved}
                    <span className="sub">
                      {share(d.stats.sent, d.stats.approved)}
                      {d.stats.sentTest ? ` · с кодом проверки ${d.stats.sentTest}` : ''}
                    </span>
                  </td>
                </tr>
                {d.stats.error > 0 && (
                  <tr>
                    <td>Meta не приняла — повторим</td>
                    <td className="num">{d.stats.error}</td>
                  </tr>
                )}
                {d.stats.reasons.map((r) => (
                  <tr key={r.reason}>
                    <td>
                      Не отправлены
                      <span className="sub">{r.reason}</span>
                    </td>
                    <td className="num">{r.n}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="capi-pixels">
            <div className="af-hold">
              <span>Доступ к пикселю</span>
              <Button size="sm" onClick={() => void check()} disabled={busy}>
                {busy ? 'Проверяю…' : 'Проверить доступ'}
              </Button>
            </div>
            {d?.check?.pixels.length ? (
              <ul className="capi-pixel-list">
                {d.check.pixels.map((p) => {
                  const purchase = p.quality.find((x) => x.event === d.event);
                  return (
                    <li key={p.pixelId}>
                      <div>
                        <b>{p.name ?? `Пиксель ${p.pixelId}`}</b>
                        <span className="sub tnum">{p.pixelId}</span>
                      </div>
                      {p.access ? (
                        <Chip tone="success" dot>
                          Доступ есть
                        </Chip>
                      ) : (
                        <Chip tone="danger" dot>
                          Нет доступа
                        </Chip>
                      )}
                      {p.access ? (
                        <span className="sub">
                          {purchase?.score != null
                            ? `Качество совпадения ${d.event}: ${purchase.score.toFixed(1)} из 10`
                            : `Качество совпадения ${d.event} Meta ещё не посчитала`}
                        </span>
                      ) : (
                        <span className="sub">
                          {p.error}. Business Manager → Настройки компании → Источники данных → Наборы данных
                          (пиксели) → этот пиксель → «Назначить пользователей» → системный пользователь
                          платформы, полный доступ.
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="muted af-note">
                {d?.check
                  ? 'У ваших адсетов не нашлось пикселя.'
                  : 'Ещё не проверяли. Проверка раз в 6 часов идёт сама, пока отправка включена.'}
              </p>
            )}
            {d?.check && <p className="muted af-note">Проверено {when(d.check.at)}</p>}
          </div>

          {d && d.offers.length > 0 && (
            <fieldset className="capi-offers">
              <legend>Офферы</legend>
              {d.offers.map((o) => (
                <label key={o.id} className="integ-check">
                  <input
                    type="checkbox"
                    checked={o.on}
                    onChange={(e) => toggleOffer(o.id, e.target.checked)}
                  />
                  <span>
                    <b>{o.name}</b> <span className="tnum">{o.slug}</span>
                  </span>
                </label>
              ))}
            </fieldset>
          )}
        </>
      )}
    </Card>
  );
}
